import { FC, useEffect, useRef } from 'react';
import { debounce } from '../../../../_helpers/performanceUtils.ts';

interface Props {
	lines: number;
	children: string;
}

/**
 * A -webkit-line-clamp JS implementation, but it abbreviates text based on words not characters.
 * @param text
 * @param lines
 * @param element
 */

const abbreviateTextUntilItFitsLinesInElement = (
	text: string,
	lines: number,
	element: HTMLSpanElement | null
) => {
	if (!element) return;

	element.innerHTML = text;

	const words = text.split(' ');

	const { lineHeight } = getComputedStyle(element);

	if (!lineHeight.includes('px')) return; // TODO: future improvement - make it compatible not only with px value

	const lineHeightPx: number = Number(lineHeight.split('px')[0]);

	const maxHeight: number = lineHeightPx * lines;

	const isElemTallerThanMax = () =>
		element.getBoundingClientRect().height > maxHeight;

	let popsLeft = 500;

	while (isElemTallerThanMax() && words.length > 0 && popsLeft > 0) {
		words.pop();

		element.innerHTML = words.join(' ') + '...';

		popsLeft--;
	}
};

const LineClamp: FC<Props> = ({ lines, children: text }) => {
	const ref = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		const abbreviateText = () =>
			abbreviateTextUntilItFitsLinesInElement(text, lines, ref.current);

		abbreviateText();
		document?.fonts?.ready?.then(abbreviateText);

		const debouncedAbbreviateText = debounce(abbreviateText, 300);
		window.addEventListener('resize', debouncedAbbreviateText);

		return () => {
			window.removeEventListener('resize', debouncedAbbreviateText);
		};
	}, [text, lines]);

	return (
		<span ref={ref} aria-label={text}>
			{text}
		</span>
	);
};

export default LineClamp;
